import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import useTranslation from '@/hooks/useTranslation';
import PropTypes from 'prop-types';

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    marginBottom: 8,
    maxWidth: '100%',
  },
  card: {
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: isDark ? 'rgba(239, 68, 68, 0.20)' : 'rgba(239, 68, 68, 0.15)',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 14,
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
  },
  title: {
    marginBottom: 3,
  },
  body: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    paddingTop: 14,
  },
  bodyText: {
    lineHeight: 22,
  },
});

function ContentModerationMessage({ message }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { t } = useTranslation();

  // Use the moderationMessage field if available, otherwise fall back to content
  const errorText = message?.moderationMessage || message?.content || t('messages.moderation.cantHelp');

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Icon
              name="shield"
              size={20}
              color="#EF4444"
              solid
            />
          </View>
          <View style={styles.headerText}>
            <Text size="n" weight="semibold" color="primary" style={styles.title}>
              {t('messages.moderation.unableToProcess')}
            </Text>
            <Text size="s" color="secondary">
              {t('messages.moderation.couldNotComplete')}
            </Text>
          </View>
        </View>

        {/* Body */}
        <View style={styles.body}>
          <Text size="n" color="secondary" style={styles.bodyText}>
            {errorText}
          </Text>
        </View>
      </View>
    </View>
  );
}

ContentModerationMessage.propTypes = {
  message: PropTypes.shape({
    content: PropTypes.string,
    moderationMessage: PropTypes.string,
  }),
};

ContentModerationMessage.displayName = 'ContentModerationMessage';

export default React.memo(ContentModerationMessage);
